export interface HistoryCommand {
  do: () => void
  undo: () => void
  /** If true, squash into the previous command when tags match */
  squash?: boolean
  /** Discriminator so only semantically related commands get squashed */
  tag?: string
}

const MAX_UNDO_STACK = 200

export class HistoryManager {
  private undoStack: HistoryCommand[] = []
  private redoStack: HistoryCommand[] = []
  private listeners = new Set<() => void>()

  execute(command: HistoryCommand) {
    command.do()
    if (command.squash && this.undoStack.length > 0) {
      const prev = this.undoStack[this.undoStack.length - 1]
      // Only squash when both commands share the same tag
      if (command.tag && command.tag === prev.tag) {
        this.undoStack[this.undoStack.length - 1] = {
          do: command.do,
          undo: prev.undo,
          tag: command.tag,
        }
      } else {
        this.undoStack.push(command)
      }
    } else {
      this.undoStack.push(command)
    }
    if (this.undoStack.length > MAX_UNDO_STACK) {
      this.undoStack.splice(0, this.undoStack.length - MAX_UNDO_STACK)
    }
    this.redoStack.length = 0
    this.notify()
  }

  /** Apply a change without recording it in history */
  applyEphemeral(fn: () => void) {
    fn()
  }

  undo() {
    const command = this.undoStack.pop()
    if (!command) return
    command.undo()
    this.redoStack.push(command)
    this.notify()
  }

  redo() {
    const command = this.redoStack.pop()
    if (!command) return
    command.do()
    this.undoStack.push(command)
    this.notify()
  }

  get canUndo() {
    return this.undoStack.length > 0
  }

  get canRedo() {
    return this.redoStack.length > 0
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  clear() {
    this.undoStack.length = 0
    this.redoStack.length = 0
    this.notify()
  }

  private notify() {
    for (const listener of this.listeners) listener()
  }
}
